import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Button } from '../design-system/atoms/Button';
import { Card } from '../design-system/atoms/Card';
import { C, F } from '../design-system/tokens';
import { useAuthStore } from '../domains/auth/authStore';
import { getDefaultAppRoute } from '../domains/auth/companyRole';
import { isSupabaseConfigured } from '../domains/auth/supabaseClient';

/**
 * Нормализует ошибки auth-flow в нейтральные пользовательские сообщения.
 *
 * Цель — НЕ раскрывать существование email через текст ошибки (user
 * enumeration). Supabase и наш код могут вернуть разные сообщения:
 * «Invalid login credentials», «Email not confirmed», «Компания
 * деактивирована», «Профиль пользователя не найден» — все они палят
 * атакующему какие email есть в системе.
 *
 * Поэтому любые ошибки авторизации сводим к одному нейтральному
 * сообщению. Технические/сетевые проблемы — отдельным сообщением,
 * чтобы юзер понимал что это не его пароль.
 *
 * Оригинальный текст логируется в console для отладки разработчиком —
 * через DevTools он виден только владельцу устройства.
 */
function normalizeAuthError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  console.warn('[Login] auth error:', raw);

  const lower = raw.toLowerCase();

  // Сетевые / технические — отдельное сообщение
  if (
    lower.includes('network') ||
    lower.includes('fetch') ||
    lower.includes('timeout') ||
    lower.includes('failed to fetch')
  ) {
    return 'Не удалось подключиться. Проверьте интернет и попробуйте ещё раз.';
  }

  // Серверная конфигурация — отдельное сообщение
  if (lower.includes('not configured') || lower.includes('misconfigur')) {
    return 'Сервис временно недоступен. Попробуйте позже.';
  }

  // Rate limit — отдельно, юзер должен понимать что блок временный
  if (lower.includes('rate') || lower.includes('too many')) {
    return 'Слишком много попыток. Подождите несколько минут и попробуйте снова.';
  }

  // Всё остальное (включая Invalid credentials, Email not confirmed,
  // Компания деактивирована, Профиль не найден) — единое нейтральное.
  return 'Неверный email или пароль.';
}

export function Login() {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const login = useAuthStore((state) => state.login);
  const loading = useAuthStore((state) => state.loading);
  const error = useAuthStore((state) => state.error);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  if (user) {
    return <Navigate to={getDefaultAppRoute(user, user.company_membership_id)} replace />;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLocalError(null);
    try {
      await login(email, password);
    } catch (err) {
      setLocalError(normalizeAuthError(err));
    }
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        background: C.bg,
      }}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 400,
      }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          padding: '24px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* Logo + heading */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
          {/* Logo mark — единый источник правды: public/icon-192.png. Та же
              иконка, что в push-уведомлениях, бейджах и на главном экране
              телефона после установки PWA. */}
          <img
            src="/icon-192.png"
            alt="Кворум"
            width={64}
            height={64}
            style={{
              borderRadius: 14,
              boxShadow: `0 8px 28px ${C.acc}55`,
              display: 'block',
            }}
          />
          <div>
            <div
              style={{
                fontFamily: F.head,
                fontWeight: 800,
                fontSize: 26,
                color: C.txt,
                letterSpacing: '-.3px',
              }}
            >
              Кворум
            </div>
            <div style={{ color: C.sub, marginTop: 4, fontSize: 13, lineHeight: 1.5 }}>
              Войдите, чтобы продолжить работу
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ color: C.sub, fontSize: 11, fontFamily: F.head, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase' }}>
              Email
            </span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@company.com"
              autoComplete="email"
              type="email"
              style={{
                background: C.surf,
                border: `1px solid ${C.b}`,
                color: C.txt,
                borderRadius: 10,
                padding: '12px 14px',
                outline: 'none',
                fontSize: 14,
                fontFamily: F.body,
              }}
            />
          </label>

          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ color: C.sub, fontSize: 11, fontFamily: F.head, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase' }}>
              Пароль
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              style={{
                background: C.surf,
                border: `1px solid ${C.b}`,
                color: C.txt,
                borderRadius: 10,
                padding: '12px 14px',
                outline: 'none',
                fontSize: 14,
                fontFamily: F.body,
              }}
            />
          </label>

          {localError || error ? (
            <div
              style={{
                padding: '9px 12px',
                borderRadius: 8,
                background: C.reS,
                border: `1px solid ${C.re}44`,
                color: C.re,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              {localError || (error ? normalizeAuthError(error) : '')}
            </div>
          ) : null}

          <Button
            type="submit"
            fw
            sz="lg"
            loading={loading}
            disabled={!isSupabaseConfigured && loading}
            ch="ВОЙТИ"
          />

          {/* 1.30.0 / P0-4 — ссылка на восстановление пароля.
              Прячем в local-demo-режиме: там нет реальной отправки писем. */}
          {isSupabaseConfigured ? (
            <div style={{ textAlign: 'center', marginTop: 2 }}>
              <Link
                to="/forgot-password"
                style={{
                  color: C.sub,
                  fontSize: 12,
                  textDecoration: 'none',
                  fontFamily: F.head,
                  fontWeight: 600,
                  letterSpacing: '.4px',
                }}
              >
                Забыли пароль?
              </Link>
            </div>
          ) : null}
        </form>
      </Card>

      {/* 1.34.0 — Юридические документы. Доступны без логина для любого,
          кто открыл сервис. После регистрации показывается обязательное
          согласие через LegalConsentGate. */}
      <div style={{
        marginTop: 18,
        display: 'flex',
        justifyContent: 'center',
        gap: 14,
        flexWrap: 'wrap',
        fontSize: 11,
        color: C.sub,
        fontFamily: F.head,
        fontWeight: 600,
        letterSpacing: '.3px',
      }}>
        <Link to="/legal/privacy" style={{ color: C.sub, textDecoration: 'none' }}>
          Политика ПДн
        </Link>
        <span style={{ color: C.dim }}>·</span>
        <Link to="/legal/consent" style={{ color: C.sub, textDecoration: 'none' }}>
          Согласие
        </Link>
        <span style={{ color: C.dim }}>·</span>
        <Link to="/legal/terms" style={{ color: C.sub, textDecoration: 'none' }}>
          Соглашение
        </Link>
      </div>
      </div>
    </div>
  );
}
